import React from "react";
import cn from "classnames";
import "./selectableCard.scss";
import { Card } from "semantic-ui-react";
import { MoboLink } from "components/MoboLink/MoboLink";
import { Tooltip } from "components/common/tooltip";

interface SelectableCardProps<T> {
  id: T;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  onCardClick?: ((id: T) => void) | null;
  onSelect?: (id: T) => void;
  url?: string;
  moboAccountId?: number;
  tooltipContent?: string;
}

function SelectableCard<T extends number | string>({
  id,
  children,
  selected,
  disabled,
  className,
  onCardClick,
  onSelect,
  url,
  moboAccountId,
  tooltipContent,
}: SelectableCardProps<T>): JSX.Element {
  const cardClassNames = cn("selectable-card", className, {
    "no-hover-state": !onCardClick,
    selected,
    disabled,
    clickableCard: url || onCardClick,
  });

  const onClick = (e: React.MouseEvent<HTMLElement>): void => {
    let targetClassName = (e.target as HTMLElement).className;
    let parentClassName = (e.target as HTMLElement).parentElement?.className || "";
    const checkboxClassName = "selection-checkbox";
    const radioButtonClassName = "radio checkbox";
    const btnClassName = "button";

    const containsCheckbox = (): boolean => {
      return (
        targetClassName.includes(checkboxClassName) ||
        targetClassName.includes(radioButtonClassName) ||
        parentClassName.includes(checkboxClassName) ||
        parentClassName.includes(radioButtonClassName)
      );
    };

    if (!disabled && containsCheckbox()) {
      onSelect && onSelect(id);
      e.preventDefault();
      e.stopPropagation();
    } else if (!targetClassName.includes(btnClassName) && !parentClassName.includes(btnClassName)) {
      onCardClick && onCardClick(id);
    }
  };

  if (!url) {
    return tooltipContent ? (
      <Card className={cardClassNames} key={id} onClick={onClick} data-testid="selectable-card" data-id={id}>
        <Tooltip
          showAlways={disabled}
          content={tooltipContent}
          target={<div className="tooltip-wrapper">{children}</div>}
        />
      </Card>
    ) : (
      <Card className={cardClassNames} key={id} onClick={onClick} data-testid="selectable-card" data-id={id}>
        {children}
      </Card>
    );
  }

  return tooltipContent ? (
    <MoboLink
      to={url}
      accountId={moboAccountId}
      target={moboAccountId ? "_blank" : undefined}
      className={`ui card ${cardClassNames}`}
      key={id}
      onClick={onClick}
      data-testid="selectable-card"
      data-id={id}
    >
      <Tooltip
        showAlways={disabled}
        content={tooltipContent}
        target={<div className="tooltip-wrapper">{children}</div>}
      />
    </MoboLink>
  ) : (
    <MoboLink
      to={url}
      accountId={moboAccountId}
      target={moboAccountId ? "_blank" : undefined}
      className={`ui card ${cardClassNames}`}
      key={id}
      onClick={onClick}
      data-testid="selectable-card"
      data-id={id}
    >
      {children}
    </MoboLink>
  );
}

SelectableCard.defaultProps = {
  onSelect: (id: number | string): void => {
    console.warn(`No select handler on card. Card id: ${id}`);
  },
};

export default SelectableCard;
